import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useFinancial} from '../../../context/FinancialContext';
import QuickAddForm from './QuickAddForm';
import {fmt, FREQ_ABBR} from './walkthroughUtils';

const {FiInfo, FiTrash2, FiTrendingUp, FiCalendar} = FiIcons;
const FIELDS = [
  {key: 'currentAge', label: 'Current Age'},
  {key: 'targetAge', label: 'Retirement Age (Target)'},
  {key: 'expectedROI', label: 'Expected ROI %', step: '0.1'},
  {key: 'startingNetWorth', label: 'Invested Capital', prefix: '$'}
];

export const BaselineTool = () => {
  const {state, dispatch} = useFinancial();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map(field => (
          <div key={field.key} className="space-y-1">
            <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">{field.label}</label>
            <div className="relative">
              {field.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">$</span>}
              <input type="number" min="0" step={field.step} value={state.goals[field.key] ?? ''} onChange={event => dispatch({type: 'UPDATE_GOALS', payload: {[field.key]: parseFloat(event.target.value) || 0}})} className={`w-full ${field.prefix ? 'pl-6' : 'pl-3'} pr-3 py-3 bg-slate-50 rounded-xl font-black text-sm text-slate-900 border-none outline-none focus:ring-2 focus:ring-slate-900`} />
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex gap-2 items-start">
        <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0 text-xs" />
        <p className="text-[10px] font-bold text-blue-800">These values update the dashboard goal bar as you type. A conservative ROI assumption can make planning more resilient.</p>
      </div>
      <div className="space-y-2">
        <p className="text-[9px] font-black uppercase text-blue-600 tracking-widest ml-1">Monthly Invested</p>
        <QuickAddForm kind="investment" placeholder="e.g. Roth IRA,401(k)" />
      </div>
    </div>
  );
};

export const BaselineVisual = () => {
  const {state, dispatch} = useFinancial();
  const {goals, calculations, investmentBuckets} = state;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiCalendar} className="text-blue-600" />
          <p className="text-[9px] uppercase tracking-widest text-slate-500 font-black">Your planning horizon</p>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div><p className="text-[9px] font-bold text-slate-400">Today</p><p className="text-2xl font-black text-slate-900">Age {goals.currentAge}</p></div>
          <div className="flex-1 h-1.5 bg-blue-100 rounded-full relative mx-2"><span className="absolute right-0 -top-1.5 w-4 h-4 rounded-full bg-blue-600 ring-4 ring-blue-100" /></div>
          <div className="text-right"><p className="text-[9px] font-bold text-slate-400">Your goal</p><p className="text-2xl font-black text-blue-600">Age {goals.targetAge}</p></div>
        </div>
        <p className="text-[10px] font-semibold text-slate-500 mt-4">{calculations.yearsToGoal} years to build toward your financial independence goal.</p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <div className="min-w-0 p-3 sm:p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center min-h-5">
            <p className="text-[9px] font-black uppercase text-slate-400 whitespace-nowrap">Invested capital</p>
          </div>
          <p className="text-lg font-black text-slate-900">{fmt(goals.startingNetWorth)}</p>
        </div>
        <div className="min-w-0 p-3 sm:p-4 bg-blue-50 rounded-2xl border border-blue-100">
          <div className="flex items-center gap-1 min-h-5">
            <SafeIcon icon={FiTrendingUp} className="text-blue-600 text-[10px] flex-shrink-0" />
            <p className="text-[9px] font-black uppercase text-blue-500 whitespace-nowrap tracking-tight">Expected return</p>
          </div>
          <p className="text-lg font-black text-blue-900">{goals.expectedROI}%</p>
        </div>
      </div>
      <div className="p-4 bg-white rounded-2xl border border-slate-100">
        <p className="text-[9px] font-black uppercase text-slate-400">Monthly invested</p>
        <p className="text-xl font-black text-blue-600">{fmt(calculations.monthlyInvested)}</p>
      </div>
      <div className="space-y-2">
        <p className="text-[9px] font-black uppercase text-blue-600 tracking-widest">Investments in Budget</p>
        {investmentBuckets.length === 0 && <p className="text-[10px] font-bold text-slate-400">No investments added yet.</p>}
        {investmentBuckets.map(bucket => (
          <div key={bucket.id} className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-blue-100">
            <div className="min-w-0">
              <p className="text-[11px] font-black text-slate-900 truncate">{bucket.name}</p>
              <p className="text-[9px] font-bold text-slate-400">{fmt(bucket.amount)}/{FREQ_ABBR[bucket.frequency] || 'mo'}</p>
            </div>
            <button onClick={() => dispatch({type: 'REMOVE_INVESTMENT', id: bucket.id})} aria-label={`Remove ${bucket.name}`} className="p-1.5 text-slate-300 hover:text-rose-500">
              <SafeIcon icon={FiTrash2} className="text-xs" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};